import { useState, useEffect, type ReactNode, useRef, useMemo } from 'react';
import useSWR from 'swr';
import apiClient from '../services/api';
import { notifySessionExpired } from '../services/sessionExpiry';
import { useAuth } from './AuthContext';
import { GenieContext, type Message } from './GenieContext';

const delay = (ms: number, signal?: AbortSignal) => {
    return new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
            return reject(new DOMException('Request aborted by user', 'AbortError'));
        }
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(new DOMException('Request aborted by user', 'AbortError'));
        }, { once: true });
    });
};

export function GenieProvider({ children }: { children: ReactNode }) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isThinking, setIsThinking] = useState(false);
    const [thinkingStage, setThinkingStage] = useState<string | null>(null);
    const { isLoggedIn } = useAuth();
    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

    const { data: conversationData, mutate } = useSWR(
        isLoggedIn ? '/genie/start_conversation' : null,
        (url) => apiClient.post(url).then(res => res.data),
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
            onSuccess: (data) => {
                if (data?.conversation_id) {
                    setMessages([]);
                    setActiveConversationId(data.conversation_id);
                }
            }
        }
    );

    const conversationId = activeConversationId || conversationData?.conversation_id || null;
    const abortControllerRef = useRef<AbortController | null>(null);

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const sendMessage = async (text: string) => {
        if (!text.trim()) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const timestamp = new Date().toISOString();
        const userMsg: Message = {
            type: 'user',
            content: text,
            timestamp: formatTime(new Date()),
            messageId: `user-${timestamp}-${Math.random().toString(36).substring(2, 9)}`
        };


        setMessages(prev => [...prev, userMsg]);
        setIsThinking(true);
        setThinkingStage("Connecting to Genie");

        try {
            const extraHeaders: Record<string, string> = {
                'Content-Type': 'application/json'
            };
            if (conversationId) {
                extraHeaders['X-Conversation-Id'] = conversationId;
            }

            // 1. POST to /api/genie/messages (Initiate Job)
            const postResponse = await fetch('/api/genie/messages', {
                method: 'POST',
                headers: extraHeaders,
                body: JSON.stringify({ message: text }),
                signal: abortControllerRef.current.signal
            });

if (postResponse.status === 401) {
                const errBody = await postResponse.json().catch(() => ({}));
                notifySessionExpired(errBody?.login_url);
                setIsThinking(false);
                setThinkingStage(null);
                return;
            }
            
if (!postResponse.ok) {
                const errBody = await postResponse.json().catch(() => ({}));
                throw new Error(errBody.error || `Request failed! status: ${postResponse.status}`);
            }

            const initData = await postResponse.json();
            const returnedConvId = initData.conversation_id;
            const messageId = initData.message_id;

            if (!returnedConvId || !messageId) {
                throw new Error('Backend did not return valid conversation_id and message_id.');
            }

            if (!activeConversationId) {
                setActiveConversationId(returnedConvId);
            }

            // 2. GET to /api/genie/messages (Long Poll on the EXACT same path
            const POLL_INTERVAL_MS = 1500;
            const MAX_TOTAL_TIMEOUT_MS = 4 * 60 * 1000;
            const pollStartTime = Date.now();
            let isCompleted = false;

            while (!isCompleted && (Date.now() - pollStartTime) < MAX_TOTAL_TIMEOUT_MS) {
                if (abortControllerRef.current?.signal.aborted) {
                    throw new DOMException('Request aborted by user', 'AbortError');
                }

                const Params = new URLSearchParams({
                    conversation_id: returnedConvId,
                    message_id: messageId
                });

                const pollResponse = await fetch(`/api/genie/messages?${Params.toString()}`, {
                    method: 'GET',
                    headers: { 
                        'Accept': 'application/json',
                        'X-Conversation-Id': returnedConvId
                    },
                    credentials: 'same-origin',
                    signal: controller.signal
                });
                
                if (pollResponse.status === 401) {
                    const errBody = await pollResponse.json().catch(() => ({}));
                    notifySessionExpired(errBody?.login_url);
                    setIsThinking(false);
                    setThinkingStage(null);
                    return;
                }
                if (!pollResponse.ok) {
                    const errBody = await pollResponse.json().catch(() => ({}));
                    throw new Error(errBody.error || `Polling error! status: ${pollResponse.status}`);
                }

                const data = await pollResponse.json();

                if (data.thinking_stage){
                    setThinkingStage(data.thinking_stage)
                }
                if (data.conversation_id && !activeConversationId) {
                    setActiveConversationId(data.conversation_id);
                }
                if (data.status == 'FAILED') {
                    throw new Error(data.error || 'Databricks Genie failed to process your request.');
                }

                if (data.isCompleted && data.status !== 'COMPLETED') {
                    throw new Error(data.error || `Current Databrick Genie status ${data.status}` || data.thinking_stage )
                }
                if (data.isCompleted || data.status === 'COMPLETED') {
                    const result = data.result || {};
                    const finalContent = data.reply || result.message || '';

                    const genieMsg: Message = {
                        type: 'genie',
                        content: finalContent,
                        timestamp: formatTime(new Date()),
                        suggestedQuestions: result.suggested_questions || [],
                        messageId: messageId,
                        attachmentId: result.attachment_id || result.attachments?.[0]?.id,
                        chartRecommendation: result.chart_recommendation || undefined,
                    };

                    setMessages(prev => [...prev, genieMsg]);
                    setIsThinking(false);
                    setThinkingStage(null);
                    isCompleted = true;
                    break;
                }
                await delay(POLL_INTERVAL_MS, controller.signal);
            }

            if (!isCompleted) {
                throw new Error('Request timed out waiting for Genie response.');
            }
        } catch (error: any) {
            if (error.name === 'AbortError' || error.message === 'canceled') {
                return;
            }
            console.error("Genie API error encountered:", error);

            const displayMessage = error instanceof Error ? error.message : "Unable to process your request.";
            const errorMsg: Message = {
                type: 'genie',
                content: `Error: ${displayMessage}`,
                timestamp: formatTime(new Date()),
                messageId: `error-${Date.now()}`
            };

            setMessages(prev => [...prev, errorMsg]);
            setIsThinking(false);
            setThinkingStage(null);
} finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
        }
    };

    const submitFeedback = async (messageId: string, isUseful: boolean) => {
        if (!messageId || messageId.startsWith('error-') || !conversationId) {
            return;
        }

        try {
            await apiClient.post(
                `/genie/messages/${messageId}/feedback`,
                { is_useful: isUseful },
                { headers: { 'X-Conversation-Id': conversationId } }
            );
        } catch (err) {
            console.error("Failed to submit feedback", err);
        }
    };

    const restartConversation = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsThinking(false);
        setThinkingStage(null);
        setMessages([]);
        setActiveConversationId(null);
        try {
            await apiClient.post('/genie/restart_conversation');
            mutate();
        } catch (err) {
            console.error("Failed to restart conversation", err);
        }
    };

    const getDownloadUrl = async (messageId: string, attachmentId: string): Promise<boolean> => {
        try {
            const config = {
                responseType: 'blob' as const,
                ...(conversationId ? { headers: { 'X-Conversation-Id': conversationId } } : {})
            };
            const response = await apiClient.get(`/genie/messages/${messageId}/attachments/${attachmentId}/download`, config);

            const file = new Blob([response.data as BlobPart], { type: 'text/csv' });
            const fileURL = URL.createObjectURL(file);
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', 'genie_result.csv');
            document.body.appendChild(fileLink);
            fileLink.click();
            document.body.removeChild(fileLink);
            URL.revokeObjectURL(fileURL);
            return true;
        } catch (err) {
            console.error("Failed to download file", err);
            return false;
        }
    };

    const value = useMemo(() => ({
        messages,
        isThinking,
        thinkingStage,
        conversationId,
        sendMessage,
        submitFeedback,
        getDownloadUrl,
        restartConversation
    }), [messages, isThinking, thinkingStage, conversationId]);

    return (
        <GenieContext.Provider value={value}>
            {children}
        </GenieContext.Provider>
    );
}